import ExcelJs from 'exceljs';
import cloneDeep from 'lodash/cloneDeep';

import { COUNTRIES, downloadFileToClient } from '@cdaas/reusable-components-daas';
import { sanitizeEmployeeId } from './validationUtils';
import { User, UserGroup } from '../types';

const COLUMNS = [
  { name: 'Phone number' },
  { name: 'Email' },
  { name: 'Employee ID (must be unique)' },
  { name: 'Onboarding date (dd.mm.yyyy)', style: { numFmt: 'dd.mm.yyyy' } },
];

const DONT_TOUCH_THIS_ROW = "Don't touch this row!";

const createWorksheetColumnsWithStyle = (worksheet: ExcelJs.Worksheet, userGroupName: string, columns: typeof COLUMNS) =>
  (worksheet.columns = [
    {
      header: 'User group',
      style: { numFmt: '@', ...columns[0]?.style },
      width: 30,
    },
    {
      header: userGroupName,
      style: { numFmt: '@', ...columns[1]?.style },
      width: 30,
    },
    ...Array.from({ length: columns.length - 2 }, (_, index) => ({
      header: '',
      style: { numFmt: '@', ...columns[index + 2]?.style },
      width: 30,
    })),
    { header: DONT_TOUCH_THIS_ROW, width: 30 },
  ]);

const addColumnHeaders = (worksheet: ExcelJs.Worksheet, columns: typeof COLUMNS) =>
  worksheet.addRow([...columns.map(column => column.name), DONT_TOUCH_THIS_ROW]);

const makeColumnHeadersBold = (worksheet: ExcelJs.Worksheet, columns: typeof COLUMNS) => {
  for (let i = 0; i < columns.length; i++) {
    worksheet.getRow(2).getCell(i + 1).font = { bold: true };
  }
};

export const generateUsersWorkbook = (userGroups: UserGroup[]): ExcelJs.Workbook => {
  const workbook = new ExcelJs.Workbook();
  const excelTabNameMaxLength = 31;
  const prohibitedCharactersInExcelTabName = ['\\', '/', '*', '?', ':', '[', ']'];
  const userGroupNamesAsExcelTabNames: string[] = [];

  userGroups.forEach(userGroup => {
    let userGroupNameAsExcelTabName = userGroup.name
      .split('')
      .map(char => (prohibitedCharactersInExcelTabName.includes(char) ? '_' : char))
      .join('')
      .substring(0, excelTabNameMaxLength);

    // Diff possible duplicate tab names
    let i = 1;
    const originalUserGroupNameAsExcelTabName = cloneDeep(userGroupNameAsExcelTabName);
    while (userGroupNamesAsExcelTabNames.includes(userGroupNameAsExcelTabName)) {
      userGroupNameAsExcelTabName = cloneDeep(originalUserGroupNameAsExcelTabName);
      const exceedsMaxTabNameLength = userGroupNameAsExcelTabName.length + i.toString().length - excelTabNameMaxLength;
      if (exceedsMaxTabNameLength > 0) {
        userGroupNameAsExcelTabName = userGroupNameAsExcelTabName.slice(0, userGroupNameAsExcelTabName.length - exceedsMaxTabNameLength);
      }
      userGroupNameAsExcelTabName += i.toString();
      i++;
    }
    userGroupNamesAsExcelTabNames.push(userGroupNameAsExcelTabName);

    const worksheet = workbook.addWorksheet(userGroupNameAsExcelTabName);

    createWorksheetColumnsWithStyle(worksheet, userGroup.name, COLUMNS);
    addColumnHeaders(worksheet, COLUMNS);
    makeColumnHeadersBold(worksheet, COLUMNS);
  });

  return workbook;
};

export const downloadUsersTemplateSpreadsheet = async (userGroups?: UserGroup[]) => {
  if (!userGroups?.length) return;

  const workbook = generateUsersWorkbook(userGroups);
  const buffer = await workbook.xlsx.writeBuffer();
  downloadFileToClient(buffer, 'users.xlsx');
};

export const parsePhoneNumber = (phoneNumber: string): string => {
  // Removes any leading single quotes ('), equal signs (=), and all whitespace characters from the phone number
  phoneNumber = phoneNumber.replace(/^['=]+/, '').replace(/\s+/g, '');

  if (!/^\+?\d+$/.test(phoneNumber)) return '';

  const phoneNumberToCheck = phoneNumber.startsWith('+') ? phoneNumber.slice(1) : phoneNumber;
  const norwayCode = COUNTRIES.find(country => country.value === 'NO')?.code ?? '';

  if (phoneNumber.startsWith('+')) {
    return COUNTRIES.some(ccc => phoneNumberToCheck.startsWith(ccc.code)) ? phoneNumber : '';
  }

  let formattedPhoneNumber: string;
  if (phoneNumberToCheck.length <= 8) {
    formattedPhoneNumber = `+${norwayCode}${phoneNumberToCheck}`;
  } else if (COUNTRIES.some(ccc => phoneNumberToCheck.startsWith(ccc.code))) {
    formattedPhoneNumber = `+${phoneNumberToCheck}`;
  } else {
    formattedPhoneNumber = `+${norwayCode}${phoneNumberToCheck}`;
  }

  return formattedPhoneNumber;
};

export enum ExcelUserImportErrorType {
  NoUsers = 'no_users',
  FormatNotSupported = 'format_not_supported',
  General = 'general',
}

class ExcelUserImportError extends Error {
  type: ExcelUserImportErrorType;
  constructor(message: string | undefined, type: ExcelUserImportErrorType) {
    super(message);
    this.name = 'ExcelUserImportError';
    this.type = type;
  }
}

const parsePhoneNumberValue = (phone_number: unknown): string => {
  // Handle the case when the phone number is written with '+' in front of it when Excel handles it as a formula
  if (phone_number !== null && typeof phone_number === 'object') {
    phone_number = String((phone_number as { result: string | number }).result);
  }
  if (phone_number !== undefined && typeof phone_number !== 'string' && typeof phone_number !== 'number') {
    throw new ExcelUserImportError('Error: phone number not formatted as text or number', ExcelUserImportErrorType.FormatNotSupported);
  }
  return typeof phone_number === 'number' ? phone_number.toString() : (phone_number ?? '');
};

const parseEmailValue = (email_original: unknown): string => {
  const email = email_original && typeof email_original === 'object' && 'text' in email_original ? email_original.text : email_original;

  if (email !== undefined && typeof email !== 'string') {
    throw new ExcelUserImportError('Error: email not formatted as text', ExcelUserImportErrorType.FormatNotSupported);
  }
  return email ?? '';
};

const parseEmployeeIdValue = (employee_id: unknown): string => {
  if (employee_id !== undefined && typeof employee_id !== 'string' && typeof employee_id !== 'number') {
    throw new ExcelUserImportError('Error: employee id not formatted as text or number', ExcelUserImportErrorType.FormatNotSupported);
  }
  return typeof employee_id === 'number' ? employee_id.toString() : (employee_id ?? '');
};

const parseOnboardingDateValue = (onboarding_date: unknown): string => {
  if (!(onboarding_date instanceof Date || typeof onboarding_date === 'string' || onboarding_date === undefined)) {
    throw new ExcelUserImportError('Error: onboarding date not formatted as text', ExcelUserImportErrorType.FormatNotSupported);
  }

  if (onboarding_date instanceof Date) {
    const day = onboarding_date.getDate().toString().padStart(2, '0');
    const month = (onboarding_date.getMonth() + 1).toString().padStart(2, '0');
    const year = onboarding_date.getFullYear();
    return `${day}.${month}.${year}`;
  }
  return onboarding_date ?? '';
};

const parseRowValues = (row: ExcelJs.Row): { phoneNumber: string; email: string; employeeId: string; onboardingDate: string } => {
  const [, phone_number, email_original, employee_id, onboarding_date] = row.values as (object | string | number)[];

  const phoneNumber = parsePhoneNumberValue(phone_number);
  const email = parseEmailValue(email_original);
  const employeeId = parseEmployeeIdValue(employee_id);
  const onboardingDate = parseOnboardingDateValue(onboarding_date);

  return { phoneNumber, email, employeeId, onboardingDate };
};

const convertDateFormat = (value: string): string | null => {
  if (value.trim() === '') return null;

  const match = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(value);
  if (match) {
    const [, day, month, year] = match;
    return `${year}-${month}-${day}`;
  }

  return value; // Return the original value if it doesn't match the pattern
};

let counter = 0;

const createUser = (
  phoneNumber: string,
  email: string,
  employeeId: string,
  onboardingDate: string,
  userGroup: UserGroup | undefined,
  userGroupName: string,
): User => ({
  name: '',
  phone_number: parsePhoneNumber(phoneNumber),
  id: `${Date.now()}-${counter++}`,
  email,
  employee_id: sanitizeEmployeeId(employeeId),
  prescheduled_onboarding_at: convertDateFormat(onboardingDate),
  offboarding_at: null,
  user_group: userGroup ?? {
    name: userGroupName,
    default_user_roles: [],
    inherited_invoicing_account: { name: '' },
  },
  user_roles: { effective: [], user: [], user_group: [] },
  allowed_procurements: [],
  allow_access_without_bank_id: false,
  inherited_invoicing_account: { name: '' },
});

export const importUsersSpreadsheet = async (
  file?: File,
  userGroups?: UserGroup[],
  successCallback?: (users: User[]) => void,
  errorCallback?: (error: ExcelUserImportErrorType) => void,
) => {
  if (!userGroups || !file) return;
  const fileReader = new FileReader();
  fileReader.readAsArrayBuffer(file);
  fileReader.onload = async () => {
    try {
      const workbook = new ExcelJs.Workbook();
      const buffer = fileReader.result as ExcelJs.Buffer;
      if (buffer) {
        await workbook.xlsx.load(buffer);
        const users: User[] = [];
        for (const worksheet of workbook.worksheets) {
          const userGroupName = worksheet.getRow(1).getCell(2).text;
          const userGroup = userGroups.find(userGroup => userGroup.name === userGroupName);
          worksheet.eachRow((row, rowIndex) => {
            if (rowIndex < 3) return true;

            const { phoneNumber, email, employeeId, onboardingDate } = parseRowValues(row);
            const user = createUser(phoneNumber, email, employeeId, onboardingDate, userGroup, userGroupName);
            users.push(user);
          });
        }
        if (users.length === 0 && errorCallback) {
          throw new ExcelUserImportError('Error: no users in given excel template', ExcelUserImportErrorType.NoUsers);
        } else if (successCallback) {
          successCallback(users);
        }
      } else {
        throw new ExcelUserImportError('Error: creating buffer of excel template file failed', ExcelUserImportErrorType.General);
      }
    } catch (error) {
      if (errorCallback) {
        if (error instanceof ExcelUserImportError && Object.values(ExcelUserImportErrorType).includes(error.type)) {
          errorCallback(error.type);
        } else {
          errorCallback(ExcelUserImportErrorType.General);
        }
      }
    }
  };
};
